import { useState, useCallback, useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, createConnectQueryKey, useTransport } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { useStore } from '@tanstack/react-store'
import { create } from '@bufbuild/protobuf'
import {
  AlertTriangle,
  Bell,
  Inbox,
  Calendar,
  FileText,
  DollarSign,
  Receipt,
  UserPlus,
  BookOpen,
  Mail,
  ChevronRight,
  CheckCheck,
  ArchiveX,
  Check,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatRelativeTime } from '@/lib/date-utils'
import { useCurrentUser } from '@/stores/user.store'
import { organizationStore } from '@/stores/organization.store'
import { useNotificationRealtime } from '@/hooks/use-notification-realtime'
import {
  listInAppNotifications,
  getUnreadNotificationCount,
  markNotificationsRead,
  archiveNotifications,
} from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  ListInAppNotificationsRequestSchema,
  GetUnreadNotificationCountRequestSchema,
  MarkNotificationsReadRequestSchema,
  ArchiveNotificationsRequestSchema,
} from '@/gen/cortado/v1/notifications_pb'
import type { InAppNotification } from '@/gen/cortado/v1/notifications_pb'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

type PopoverContentProps = React.ComponentProps<typeof PopoverContent>
type PopoverSide = PopoverContentProps['side']
type PopoverAlign = NonNullable<PopoverContentProps['align']>

// ─── Types ───────────────────────────────────────────────────────────────────

type NotificationStyle = {
  icon: typeof Bell
  color: string
  bg: string
  border: string
}

type TimeGroup = {
  label: string
  items: InAppNotification[]
}

type TabFilter = 'all' | 'unread'

// ─── Notification type → visual mapping (aligned with activity-feed-card) ────

const notificationStyles: Record<string, NotificationStyle> = {
  inquiry:  { icon: UserPlus,  color: 'text-blue-600',    bg: 'bg-blue-500/10',    border: 'border-l-blue-500' },
  lead:     { icon: UserPlus,  color: 'text-blue-600',    bg: 'bg-blue-500/10',    border: 'border-l-blue-500' },
  quote:    { icon: FileText,  color: 'text-purple-600',  bg: 'bg-purple-500/10',  border: 'border-l-purple-500' },
  invoice:  { icon: Receipt,   color: 'text-amber-600',   bg: 'bg-amber-500/10',   border: 'border-l-amber-500' },
  payment:  { icon: DollarSign,color: 'text-emerald-600', bg: 'bg-emerald-500/10', border: 'border-l-emerald-500' },
  booking:  { icon: BookOpen,  color: 'text-indigo-600',  bg: 'bg-indigo-500/10',  border: 'border-l-indigo-500' },
  event:    { icon: Calendar,  color: 'text-teal-600',    bg: 'bg-teal-500/10',    border: 'border-l-teal-500' },
  email:    { icon: Mail,      color: 'text-sky-600',     bg: 'bg-sky-500/10',     border: 'border-l-sky-500' },
  error:    { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-500/10',     border: 'border-l-red-500' },
  default:  { icon: Bell,      color: 'text-muted-foreground', bg: 'bg-muted',      border: 'border-l-border' },
}

// ─── Entry point ─────────────────────────────────────────────────────────────

interface NotificationFeedProps {
  className?: string
  /** Where the feed panel opens relative to its trigger. */
  side?: PopoverSide
  align?: PopoverAlign
}

export function NotificationFeed({ className, side, align = 'end' }: NotificationFeedProps) {
  const { user } = useCurrentUser()
  // Read the org id from the store rather than the GetOrganization response —
  // it's the same value, available a full network tier earlier.
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)
  const [isOpen, setIsOpen] = useState(false)

  const isReady = !!user?.id && !!selectedOrganizationId

  useNotificationRealtime(user?.id)

  if (!isReady) {
    return (
      <Button variant="ghost" size="icon" className={className} disabled>
        <Bell className="h-5 w-5 text-muted-foreground" />
        <span className="sr-only">Notifications</span>
      </Button>
    )
  }

  return (
    <NotificationFeedInner
      className={className}
      side={side}
      align={align}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      organizationId={selectedOrganizationId}
    />
  )
}

// ─── Main feed ───────────────────────────────────────────────────────────────

interface NotificationFeedInnerProps {
  className?: string
  side?: PopoverSide
  align: PopoverAlign
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  organizationId: string
}

function NotificationFeedInner({
  className,
  side,
  align,
  isOpen,
  setIsOpen,
  organizationId,
}: NotificationFeedInnerProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const transport = useTransport()
  const [tab, setTab] = useState<TabFilter>('all')

  // ── Data fetching ──────────────────────────────────────────────────────────

  const notificationsQuery = useQuery(
    listInAppNotifications,
    create(ListInAppNotificationsRequestSchema, { organizationId, limit: 20 }),
  )

  const unreadQuery = useQuery(
    getUnreadNotificationCount,
    create(GetUnreadNotificationCountRequestSchema, { organizationId }),
  )

  const unreadCount = unreadQuery.data?.count ?? 0
  const allItems = notificationsQuery.data?.notifications ?? []
  const hasLoaded = notificationsQuery.isSuccess || !!notificationsQuery.data
  const isLoading = notificationsQuery.isLoading && !hasLoaded

  // Filter by tab
  const filteredItems = useMemo(() => {
    if (tab === 'unread') return allItems.filter((n) => !n.readAt)
    return allItems.slice(0, 20)
  }, [allItems, tab])

  // Group by time period
  const timeGroups = useMemo(() => groupByTime(filteredItems), [filteredItems])

  // ── Mutations ──────────────────────────────────────────────────────────────

  const invalidate = useCallback(() => {
    invalidateNotificationCaches(queryClient, transport)
  }, [queryClient, transport])

  const markReadMutation = useMutation(markNotificationsRead, { onSuccess: invalidate })
  const archiveMutation = useMutation(archiveNotifications, { onSuccess: invalidate })

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleClick = useCallback(
    (item: InAppNotification) => {
      if (!item.readAt) {
        markReadMutation.mutate(
          create(MarkNotificationsReadRequestSchema, {
            organizationId,
            notificationIds: [item.id],
          }),
        )
      }
      setIsOpen(false)
      const url = item.actionUrl
      if (url) {
        if (url.startsWith('/')) {
          navigate(notificationNavigateTarget(url))
        } else if (url.startsWith(window.location.origin)) {
          navigate(notificationNavigateTarget(url.replace(window.location.origin, '')))
        } else {
          window.open(url, '_blank')
        }
      }
    },
    [markReadMutation, navigate, setIsOpen, organizationId],
  )

  const handleMarkRead = useCallback(
    (e: React.MouseEvent, item: InAppNotification) => {
      e.stopPropagation()
      if (!item.readAt) {
        markReadMutation.mutate(
          create(MarkNotificationsReadRequestSchema, {
            organizationId,
            notificationIds: [item.id],
          }),
        )
      }
    },
    [markReadMutation, organizationId],
  )

  const handleArchive = useCallback(
    (e: React.MouseEvent, item: InAppNotification) => {
      e.stopPropagation()
      archiveMutation.mutate(
        create(ArchiveNotificationsRequestSchema, {
          organizationId,
          notificationIds: [item.id],
        }),
      )
    },
    [archiveMutation, organizationId],
  )

  const handleMarkAllRead = useCallback(() => {
    markReadMutation.mutate(
      create(MarkNotificationsReadRequestSchema, { organizationId, notificationIds: [] }),
    )
  }, [markReadMutation, organizationId])

  const handleClearAll = useCallback(() => {
    archiveMutation.mutate(
      create(ArchiveNotificationsRequestSchema, { organizationId, notificationIds: [] }),
    )
  }, [archiveMutation, organizationId])

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn('relative', className)}
          aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-[18px] min-w-[18px] px-1 flex items-center justify-center text-[10px] font-bold rounded-full pointer-events-none animate-in zoom-in-50 duration-200"
>
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
          <span className="sr-only">Notifications</span>
        </Button>
      </PopoverTrigger>

      <PopoverContent side={side} align={align} className="w-[400px] p-0 overflow-hidden" sideOffset={8}>
        {/* ── Header ────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold">Notifications</h3>
            {/* Tab filter */}
            <div className="flex items-center p-0.5 rounded-md bg-muted">
              <button
                onClick={() => setTab('all')}
                className={cn(
                  'px-2 py-0.5 text-[11px] font-medium rounded-[5px] transition-all duration-150',
                  tab === 'all'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                All
              </button>
              <button
                onClick={() => setTab('unread')}
                className={cn(
                  'px-2 py-0.5 text-[11px] font-medium rounded-[5px] transition-all duration-150',
                  tab === 'unread'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                Unread{unreadCount > 0 && ` (${unreadCount})`}
              </button>
            </div>
          </div>

          {/* Bulk actions */}
          <div className="flex items-center gap-0.5">
            {unreadCount > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-foreground"
                    onClick={handleMarkAllRead}
                  >
                    <CheckCheck className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  Mark all as read
                </TooltipContent>
              </Tooltip>
            )}
            {filteredItems.length > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-foreground"
                    onClick={handleClearAll}
                  >
                    <ArchiveX className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  Archive all
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>

        {/* ── Content ───────────────────────────────────────────────────── */}
        {isLoading ? (
          <NotificationSkeleton />
        ) : filteredItems.length > 0 ? (
          <div className="max-h-[420px] overflow-y-auto">
            <div className="py-1">
              {timeGroups.map((group, gi) => (
                <div key={group.label}>
                  {/* Time group header */}
                  <div className="px-4 pt-2.5 pb-1.5">
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                      {group.label}
                    </span>
                  </div>
                  {/* Items */}
                  {group.items.map((item, i) => (
                    <NotificationItem
                      key={item.id}
                      item={item}
                      index={gi * 10 + i}
                      onClick={() => handleClick(item)}
                      onMarkRead={(e) => handleMarkRead(e, item)}
                      onArchive={(e) => handleArchive(e, item)}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <EmptyNotifications tab={tab} />
        )}
      </PopoverContent>
    </Popover>
  )
}

// ─── Notification item ───────────────────────────────────────────────────────

interface NotificationItemProps {
  item: InAppNotification
  index: number
  onClick: () => void
  onMarkRead: (e: React.MouseEvent) => void
  onArchive: (e: React.MouseEvent) => void
}

function NotificationItem({ item, index, onClick, onMarkRead, onArchive }: NotificationItemProps) {
  const isUnread = !item.readAt
  const timestamp = item.createdAt
    ? new Date(Number(item.createdAt.seconds) * 1000)
    : null

  const type = resolveNotificationType(item.notificationType)
  const style = notificationStyles[type] || notificationStyles.default
  const IconComponent = style.icon

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left px-4 py-3 transition-all duration-150',
        'hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none',
        'group cursor-pointer relative',
        'border-l-2 border-l-transparent',
        // Staggered entry animation
        'animate-in fade-in-0 slide-in-from-bottom-1 duration-200',
        // Unread: colored left border + subtle tinted background
        isUnread && [style.border, 'bg-muted/20'],
      )}
      style={{ animationDelay: `${Math.min(index * 30, 150)}ms`, animationFillMode: 'backwards' }}
    >
      {/* Hover actions — top-right corner */}
      <div className="absolute top-2 right-3 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-10">
        {isUnread && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                role="button"
                tabIndex={0}
                onClick={onMarkRead}
                className="h-6 w-6 rounded-md inline-flex items-center justify-center text-muted-foreground/60 hover:text-foreground hover:bg-muted transition-colors duration-100"
              >
                <Check className="h-3.5 w-3.5" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              Mark as read
            </TooltipContent>
          </Tooltip>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              role="button"
              tabIndex={0}
              onClick={onArchive}
              className="h-6 w-6 rounded-md inline-flex items-center justify-center text-muted-foreground/60 hover:text-foreground hover:bg-muted transition-colors duration-100"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            Archive
          </TooltipContent>
        </Tooltip>
      </div>

      <div className="flex gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 pt-0.5">
          <div
            className={cn(
              'w-8 h-8 rounded-lg flex items-center justify-center transition-transform duration-150',
              'group-hover:scale-105',
              isUnread ? style.bg : 'bg-muted/60',
            )}
          >
            <IconComponent
              className={cn(
                'h-4 w-4',
                isUnread ? style.color : 'text-muted-foreground/70',
              )}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 pr-8">
          {item.title && (
            <p
              className={cn(
                'text-[13px] leading-snug',
                isUnread ? 'font-semibold text-foreground' : 'font-medium text-muted-foreground',
              )}
            >
              {item.title}
            </p>
          )}
          {item.body && (
            <p
              className={cn(
                'text-[12px] leading-relaxed mt-0.5 line-clamp-2',
                isUnread ? 'text-foreground/70' : 'text-muted-foreground/60',
              )}
            >
              {item.body}
            </p>
          )}
          <div className="flex items-center gap-1.5 mt-1">
            {/* Unread dot */}
            {isUnread && (
              <span className={cn('w-1.5 h-1.5 rounded-full', style.color.replace('text-', 'bg-'))} />
            )}
            {timestamp && (
              <span
                className={cn(
                  'text-[11px]',
                  isUnread ? 'text-muted-foreground/50' : 'text-muted-foreground/40',
                )}
              >
                {formatRelativeTime(timestamp)}
              </span>
            )}
          </div>
        </div>

        {/* Hover chevron */}
        {item.actionUrl && (
          <div className="flex-shrink-0 self-center opacity-0 group-hover:opacity-60 transition-all duration-150">
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
        )}
      </div>
    </button>
  )
}

// ─── Skeleton loading ────────────────────────────────────────────────────────

function NotificationSkeleton() {
  return (
    <div className="py-3 space-y-1">
      {/* Fake time group header */}
      <div className="px-4 pb-1">
        <Skeleton className="h-2.5 w-12" />
      </div>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex gap-3 px-4 py-3">
          <Skeleton className="w-8 h-8 rounded-lg flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3.5 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
            <Skeleton className="h-2.5 w-16" />
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Empty state ─────────────────────────────────────────────────────────────

function EmptyNotifications({ tab }: { tab: TabFilter }) {
  return (
    <div className="flex flex-col items-center justify-center py-14 px-6">
      <div className="w-12 h-12 rounded-2xl bg-muted/80 flex items-center justify-center mb-4">
        <Inbox className="h-6 w-6 text-muted-foreground/50" />
      </div>
      <p className="text-sm font-medium text-foreground mb-1">
        {tab === 'unread' ? 'All caught up' : 'No notifications yet'}
      </p>
      <p className="text-xs text-muted-foreground/70 text-center leading-relaxed max-w-[220px]">
        {tab === 'unread'
          ? "You've read everything. Nice work."
          : 'Notifications for new inquiries, payments, and more will show up here.'}
      </p>
    </div>
  )
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Splits an in-app action URL into the path and search params the router
 * expects; a query string left inside `to` would not reach validateSearch.
 */
export function notificationNavigateTarget(url: string): { to: string; search?: Record<string, string> } {
  const queryStart = url.indexOf('?')
  if (queryStart === -1) return { to: url }
  const search = Object.fromEntries(new URLSearchParams(url.slice(queryStart + 1)))
  return { to: url.slice(0, queryStart), search }
}

export function resolveNotificationType(notificationType: string): string {
  const t = notificationType.toLowerCase()
  // An automated email that failed or may not have gone out needs a person to
  // act, so it gets the error style rather than the email one.
  if (t === 'email_send_failed') return 'error'
  if (t.includes('inquiry') || t.includes('lead')) return 'inquiry'
  if (t.includes('quote')) return 'quote'
  if (t.includes('invoice')) return 'invoice'
  if (t.includes('payment')) return 'payment'
  if (t.includes('booking')) return 'booking'
  if (t.includes('event')) return 'event'
  if (t.includes('gmail') || t.includes('reconnect')) return 'email'
  // Populated on refetch only — no realtime push for inbound email (see
  // .claude/specs/email-platform-completion.md R3). Backend emitter for this
  // category (e.g. off ActivityTypeEmailReceived) is a separate, not-yet-
  // landed piece; this just makes the feed render it correctly once it exists.
  if (t.includes('email') || t.includes('inbox') || t.includes('thread')) return 'email'
  return 'default'
}

function groupByTime(items: InAppNotification[]): TimeGroup[] {
  if (items.length === 0) return []

  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterdayStart = new Date(todayStart.getTime() - 86_400_000)
  const weekStart = new Date(todayStart.getTime() - 7 * 86_400_000)

  const groups: Record<string, InAppNotification[]> = {}
  const order = ['Today', 'Yesterday', 'This week', 'Earlier']

  for (const item of items) {
    const ts = item.createdAt ? new Date(Number(item.createdAt.seconds) * 1000) : new Date(0)
    let label: string
    if (ts >= todayStart) label = 'Today'
    else if (ts >= yesterdayStart) label = 'Yesterday'
    else if (ts >= weekStart) label = 'This week'
    else label = 'Earlier'

    if (!groups[label]) groups[label] = []
    groups[label].push(item)
  }

  return order.filter((l) => groups[l]?.length).map((label) => ({ label, items: groups[label] }))
}

function invalidateNotificationCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  transport: ReturnType<typeof useTransport>,
) {
  queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({ schema: listInAppNotifications, transport, cardinality: undefined }),
  })
  queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({ schema: getUnreadNotificationCount, transport, cardinality: undefined }),
  })
}
